import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnDef, TableOptions, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PaginatedResponse } from '../../../lib/models/paginated-response';
import { Checkbox } from '../checkbox';

export type FilterParams = Record<string, string>;

export const DEFAULT_PAGE_INDEX = 0;
export const DEFAULT_PAGE_SIZE = 20;

export type ApiTableParams<T, V> = {
  queryKey: (filters: FilterParams) => [string, V];
  queryFn: (filters: FilterParams) => Promise<PaginatedResponse<T>>;
  columns: ColumnDef<T, any>[];
  isPaginated?: boolean;
  enableSelection?: boolean;
} & Omit<
  TableOptions<T>,
  | 'data'
  | 'columns'
  | 'pageCount'
  | 'state'
  | 'getCoreRowModel'
  | 'getSortedRowModel'
  | 'manualPagination'
  | 'manualSorting'
  | 'autoResetPageIndex'
>;

export const useApiTable = <T extends { _id: string | number }, V extends FilterParams = FilterParams>({
  queryKey,
  queryFn,
  columns,
  isPaginated = true,
  enableSelection = false,
  ...options
}: ApiTableParams<T, V>) => {
  const [searchParams] = useSearchParams();

  const getFilterParams = () => {
    const fp: Record<string, string> = {};
    searchParams.forEach((value, key) => (fp[key] = value));
    if (isPaginated) {
      if (!fp.pageIndex) fp.pageIndex = DEFAULT_PAGE_INDEX.toString();
      if (!fp.pageSize) fp.pageSize = DEFAULT_PAGE_SIZE.toString();
    }
    return fp;
  };

  const [filterParams, setFilterParams] = useState<FilterParams>(getFilterParams());
  const [customRowSelection, setCustomRowSelection] = useState<Record<string, T>>({});

  const {
    data: queryData,
    isLoading,
    isFetching,
    isPlaceholderData,
    error,
  } = useQuery({
    queryKey: queryKey(filterParams),
    queryFn: () => queryFn(filterParams),
    placeholderData: keepPreviousData,
  });

  const pageIndex = parseInt(filterParams.pageIndex, 10);
  const pageSize = parseInt(filterParams.pageSize, 10);
  const data = queryData?.data ?? [];
  const total = queryData?.total ?? 0;

  const cols = useMemo(() => {
    const getHeaderCheckStatus = () => {
      let isAllChecked = true;
      let isSomeChecked = false;
      for (const item of data) {
        if (!!customRowSelection[item._id]) {
          isSomeChecked = true;
        } else {
          isAllChecked = false;
        }
      }
      if (isSomeChecked && isAllChecked) return true;
      if (isSomeChecked) return 'indeterminate';
      return false;
    };

    return [
      ...(enableSelection
        ? [
            {
              id: 'select',
              header: ({ table }) => (
                <Checkbox
                  checked={getHeaderCheckStatus()}
                  onCheckedChange={(value) =>
                    setCustomRowSelection((prev) => {
                      const newVal = { ...prev };
                      for (const item of data) {
                        if (value) {
                          newVal[item._id.toString()] = item;
                        } else {
                          delete newVal[item._id.toString()];
                        }
                      }
                      return newVal;
                    })
                  }
                  aria-label="Select all"
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  checked={!!customRowSelection[row.original._id.toString()]}
                  onCheckedChange={(value) =>
                    setCustomRowSelection((prev) => {
                      const newVal = { ...prev };
                      if (value) {
                        newVal[row.original._id.toString()] = row.original;
                      } else {
                        delete newVal[row.original._id.toString()];
                      }
                      return newVal;
                    })
                  }
                  aria-label="Select row"
                />
              ),
              enableSorting: false,
              enableHiding: false,
            } as ColumnDef<T, V>,
          ]
        : []),
      ...columns,
    ];
  }, [columns, enableSelection, customRowSelection, data]);

  const table = useReactTable({
    data,
    columns: cols,
    pageCount: isPaginated ? Math.ceil(total / pageSize) : 1,
    manualPagination: true,
    manualSorting: true,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    ...options,
  });

  useEffect(() => {
    // Get other keys from searchParams and set them as filters
    const fp = getFilterParams();
    setFilterParams(fp);
  }, [searchParams]);

  return {
    table,
    data,
    total,
    error,
    isLoading: isLoading || (isFetching && isPlaceholderData),
    filter: filterParams,
    setFilter: setFilterParams,
    customRowSelection,
    selectionCount: Object.keys(customRowSelection).length,
    clearSelection: () => setCustomRowSelection({}),
  };
};
